import { useState, useRef, useEffect, useCallback } from "react";
import { Monitor, X, Minus, ExternalLink } from "lucide-react";

const MIN_W      = 600;
const MIN_H      = 400;
const DEFAULT_W  = 960;
const DEFAULT_H  = 620;
const HEADER_H   = 42;

export default function FloatingWindow({ src, title, onClose }) {
  const [pos,       setPos]       = useState(null);
  const [size,      setSize]      = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [minimized, setMinimized] = useState(false);
  const [dragging,  setDragging]  = useState(false);
  const [resizing,  setResizing]  = useState(false);

  const dragRef   = useRef(null);
  const resizeRef = useRef(null);

  // Position bottom-right on first mount
  useEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setPos({
      x: Math.max(0, vw - DEFAULT_W - 24),
      y: Math.max(0, vh - DEFAULT_H - 24),
    });
  }, []);

  // ── Drag ─────────────────────────────────────────────────────
  const onDragStart = useCallback((e) => {
    if (e.button !== 0) return;
    dragRef.current = { ox: e.clientX - pos.x, oy: e.clientY - pos.y };
    setDragging(true);
    e.preventDefault();
  }, [pos]);

  const onDragMove = useCallback((e) => {
    if (!dragRef.current) return;
    setPos({
      x: Math.max(0, Math.min(window.innerWidth  - size.w, e.clientX - dragRef.current.ox)),
      y: Math.max(0, Math.min(window.innerHeight - HEADER_H, e.clientY - dragRef.current.oy)),
    });
  }, [size]);

  const onDragEnd = useCallback(() => { dragRef.current = null; setDragging(false); }, []);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup",   onDragEnd);
    return () => {
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup",   onDragEnd);
    };
  }, [dragging, onDragMove, onDragEnd]);

  // ── Resize ───────────────────────────────────────────────────
  const onResizeStart = useCallback((e) => {
    if (e.button !== 0) return;
    resizeRef.current = { sx: e.clientX, sy: e.clientY, sw: size.w, sh: size.h };
    setResizing(true);
    e.preventDefault();
    e.stopPropagation();
  }, [size]);

  const onResizeMove = useCallback((e) => {
    if (!resizeRef.current) return;
    setSize({
      w: Math.max(MIN_W, resizeRef.current.sw + (e.clientX - resizeRef.current.sx)),
      h: Math.max(MIN_H, resizeRef.current.sh + (e.clientY - resizeRef.current.sy)),
    });
  }, []);

  const onResizeEnd = useCallback(() => { resizeRef.current = null; setResizing(false); }, []);

  useEffect(() => {
    if (!resizing) return;
    window.addEventListener("mousemove", onResizeMove);
    window.addEventListener("mouseup",   onResizeEnd);
    return () => {
      window.removeEventListener("mousemove", onResizeMove);
      window.removeEventListener("mouseup",   onResizeEnd);
    };
  }, [resizing, onResizeMove, onResizeEnd]);

  if (!pos) return null;

  return (
    <div
      style={{
        position:      "fixed",
        left:          pos.x,
        top:           pos.y,
        width:         size.w,
        height:        minimized ? HEADER_H : size.h,
        zIndex:        9999,
        background:    "var(--t-panel)",
        border:        "1px solid #4f6fff",
        borderRadius:  10,
        boxShadow:     "0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(29,78,216,0.2)",
        display:       "flex",
        flexDirection: "column",
        overflow:      "hidden",
        userSelect:    dragging || resizing ? "none" : "auto",
        transition:    "height 0.15s ease",
      }}
    >
      {/* ── Header / drag handle ── */}
      <div
        onMouseDown={onDragStart}
        style={{
          height:        HEADER_H,
          background:    "linear-gradient(135deg,#1a3a6e 0%,#1e293b 100%)",
          borderBottom:  minimized ? "none" : "1px solid #1e3a6e",
          display:       "flex",
          alignItems:    "center",
          padding:       "0 10px",
          gap:           8,
          cursor:        dragging ? "grabbing" : "grab",
          flexShrink:    0,
        }}
      >
        <Monitor size={15} color="#60a5fa" style={{ flexShrink: 0 }} />
        <span style={{
          color:         "#cbd5e1",
          fontSize:      13,
          fontWeight:    600,
          flex:          1,
          overflow:      "hidden",
          textOverflow:  "ellipsis",
          whiteSpace:    "nowrap",
        }}>
          {title || "Remote Desktop"}
        </span>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <Btn icon={<ExternalLink size={13}/>} label="Open in new tab"
            onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
            hoverColor="#94a3b8" />
          <Btn icon={<Minus size={13}/>} label={minimized ? "Restore" : "Minimize"}
            onClick={() => setMinimized(m => !m)}
            hoverColor="#fbbf24" />
          <Btn icon={<X size={13}/>} label="Close"
            onClick={onClose}
            hoverColor="#ef4444" />
        </div>
      </div>

      {/* ── iframe ── */}
      {!minimized && (
        <div style={{ flex: 1, position: "relative", background: "#000" }}>
          <iframe
            src={src}
            title={title || "Remote Desktop"}
            allow="fullscreen"
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
          {/* Intercept mouse events during drag/resize so iframe doesn't steal them */}
          {(dragging || resizing) && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 1,
              cursor:   resizing ? "se-resize" : "grabbing",
            }} />
          )}
        </div>
      )}

      {/* ── Resize handle (bottom-right corner) ── */}
      {!minimized && (
        <div
          onMouseDown={onResizeStart}
          style={{
            position:   "absolute",
            right:      0,
            bottom:     0,
            width:      18,
            height:     18,
            cursor:     "se-resize",
            zIndex:     2,
          }}
        />
      )}
    </div>
  );
}

function Btn({ icon, label, onClick, hoverColor }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={label}
      onClick={e => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:     hov ? "rgba(255,255,255,0.1)" : "none",
        border:         "none",
        borderRadius:   4,
        padding:        "3px 6px",
        cursor:         "pointer",
        color:          hov ? hoverColor : "#64748b",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        transition:     "all 0.12s",
      }}
    >
      {icon}
    </button>
  );
}
